import React from 'react';
import { 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar, 
  ListItemSecondaryAction, 
  Avatar, 
  IconButton, 
  Button, 
  Typography, 
  Box, 
  Chip, 
  Paper,
  Divider,
  CircularProgress
} from '@mui/material';
import { 
  Person, 
  Business, 
  Group, 
  Check as CheckIcon, 
  Close as CloseIcon,
  AccessTime
} from '@mui/icons-material';
import { useOrganization, Invitation } from '../context/OrganizationContext';
import { formatDistanceToNow } from 'date-fns';

// Helper function to safely format date
const formatExpirationDate = (expiresAt: string | undefined): string => {
  if (!expiresAt) return 'date not available';
  
  const date = new Date(expiresAt);
  if (isNaN(date.getTime())) {
    console.warn('Invalid date value:', expiresAt);
    return 'invalid date';
  }
  
  try {
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    console.error('Error formatting date:', error, expiresAt);
    return 'error formatting date';
  }
};

interface InvitationListProps {
  userOnly?: boolean;
  organizationId?: string;
  limit?: number;
  showActions?: boolean;
  onInvitationAction?: (action: 'accept' | 'decline' | 'cancel', invitation: Invitation) => void;
}

const InvitationList: React.FC<InvitationListProps> = ({ 
  userOnly = false, 
  organizationId,
  limit,
  showActions = true,
  onInvitationAction
}) => {
  const { 
    invitations, 
    organizationInvitations, 
    getOrganizationInvitations,
    acceptInvitation, 
    declineInvitation, 
    cancelInvitation,
    refreshInvitations,
    isLoading
  } = useOrganization();

  const [actionInProgress, setActionInProgress] = React.useState<string | null>(null);

  // Determine which invitations to display based on props
  const determineInvitations = () => {
    if (userOnly) {
      return invitations.filter(inv => inv.status === 'pending');
    } else if (organizationId) {
      return getOrganizationInvitations(organizationId).filter(inv => inv.status === 'pending');
    } else {
      // Default to user invitations
      return invitations.filter(inv => inv.status === 'pending');
    }
  };

  const displayedInvitations = determineInvitations();
  const limitedInvitations = limit ? displayedInvitations.slice(0, limit) : displayedInvitations;

  // Debug: Log invitation data to see what's available
  React.useEffect(() => {
    if (limitedInvitations.length > 0) {
      console.log('InvitationList - Sample invitation data:', limitedInvitations[0]);
    }
  }, [limitedInvitations]);

  // Helper to check if invitation is pending
  const isPendingInvitation = (invitation: Invitation): boolean => {
    const statusStr = String(invitation.status);
    return invitation.status === 'pending' || statusStr === '0' || invitation.status === 0;
  };

  const handleAcceptInvitation = async (invitation: Invitation) => {
    try {
      setActionInProgress(invitation.id);
      await acceptInvitation(invitation.id);
      if (onInvitationAction) {
        onInvitationAction('accept', invitation);
      }
    } catch (error) {
      console.error('Failed to accept invitation:', error);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDeclineInvitation = async (invitation: Invitation) => {
    try {
      setActionInProgress(invitation.id);
      await declineInvitation(invitation.id);
      if (onInvitationAction) {
        onInvitationAction('decline', invitation);
      }
    } catch (error) {
      console.error('Failed to decline invitation:', error);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleCancelInvitation = async (invitation: Invitation) => {
    try {
      setActionInProgress(invitation.id);
      await cancelInvitation(invitation.id);
      if (onInvitationAction) {
        onInvitationAction('cancel', invitation);
      }
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
    } finally {
      setActionInProgress(null);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (limitedInvitations.length === 0) {
    return (
      <Box p={2} textAlign="center">
        <Typography variant="body2" color="textSecondary">
          {userOnly 
            ? "You don't have any pending invitations." 
            : organizationId 
              ? "No pending invitations for this organization." 
              : "No pending invitations."}
        </Typography>
      </Box>
    );
  }

  return (
    <List>
      {limitedInvitations.map((invitation) => {
        const isUserInvitation = userOnly || !organizationId;
        const expiresIn = formatExpirationDate(invitation.expiresAt);
        
        return (
          <React.Fragment key={invitation.id}>
            <ListItem alignItems="flex-start">
              <ListItemAvatar>
                <Avatar>
                  {invitation.scope === 'organization' ? <Business /> : <Group />}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography component="span" variant="body1">
                      {isUserInvitation
                        ? invitation.scope === 'team' && invitation.teamName
                          ? `Invitation to join ${invitation.teamName} in ${invitation.organizationName || 'organization'}`
                          : `Invitation to join ${invitation.organizationName || 'Organization'}`
                        : `Invitation to ${invitation.email}`}
                    </Typography>
                    <Chip
                      size="small"
                      label={invitation.scope === 'organization' ? 'Organization' : 'Team'}
                      color={invitation.scope === 'organization' ? 'primary' : 'secondary'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                }
                secondary={
                  <React.Fragment>
                    <Typography component="span" variant="body2" color="textPrimary">
                      {/* Team name already shown in primary for user invitations */}
                    </Typography>
                    {isPendingInvitation(invitation) && (
                      <Box mt={0.5} display="flex" alignItems="center" gap={0.5}>
                        <AccessTime fontSize="small" color="action" />
                        <Typography variant="caption" color="textSecondary">
                          Expires {expiresIn}
                        </Typography>
                      </Box>
                    )}
                    {invitation.roleName && (
                      <Typography variant="caption" display="block">
                        Role: {invitation.roleName}
                      </Typography>
                    )}
                  </React.Fragment>
                }
              />
              {showActions && (
                <ListItemSecondaryAction>
                  {isUserInvitation ? (
                    // User view - Accept/Decline
                    <Box>
                      <IconButton 
                        edge="end" 
                        color="primary" 
                        disabled={!!actionInProgress}
                        onClick={() => handleAcceptInvitation(invitation)}
                      >
                        {actionInProgress === invitation.id ? <CircularProgress size={20} /> : <CheckIcon />}
                      </IconButton>
                      <IconButton 
                        edge="end" 
                        color="error" 
                        disabled={!!actionInProgress}
                        onClick={() => handleDeclineInvitation(invitation)}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    // Organization admin view - Cancel
                    <Button 
                      size="small" 
                      color="error" 
                      variant="outlined"
                      disabled={!!actionInProgress}
                      onClick={() => handleCancelInvitation(invitation)}
                    >
                      {actionInProgress === invitation.id ? <CircularProgress size={20} /> : 'Cancel'}
                    </Button>
                  )}
                </ListItemSecondaryAction>
              )}
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        );
      })}
      {limit && displayedInvitations.length > limit && (
        <Box p={2} textAlign="center">
          <Button variant="text" color="primary" onClick={refreshInvitations}>
            View all {displayedInvitations.length} invitations
          </Button>
        </Box>
      )}
    </List>
  );
};

export default InvitationList;